import React, { useEffect, useState, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useOutletContext } from 'react-router-dom';
import pb from '../../lib/pocketbase';
import styles from '../Dashboard.module.css';
import chantiersStyles from './Chantiers.module.css';
import { 
    LayoutGrid, List, Map as MapIcon, Plus, Search, 
    Edit2, Trash2, Camera, Clock, 
    CheckCircle, AlertCircle, Euro, User, MapPin, 
    History, CloudSun, ChevronRight, Image as ImageIcon,
    MoreVertical, Info, Scissors, Map as MapType, FileText
} from 'lucide-react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { toast } from 'react-toastify';
import ChantierForm from '../../components/dashboard/ChantierForm';

// Fix for default Leaflet icons in React
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
    iconUrl: require('leaflet/dist/images/marker-icon.png'),
    shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const blueIcon = new L.Icon({
    iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-blue.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41]
});

const Chantiers = () => {
    const { userRole, refreshData } = useOutletContext();
    const [chantiers, setChantiers] = useState([]);
    const [loading, setLoading] = useState(true);

    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('ALL');
    const [viewMode, setViewMode] = useState('LIST'); 
    const [markers, setMarkers] = useState([]);
    const [isGeocoding, setIsGeocoding] = useState(false);

    const [isFormOpen, setIsFormOpen] = useState(false);
    const [editingChantier, setEditingChantier] = useState(null);
    const [selectedJournal, setSelectedJournal] = useState(null);
    const [selectedTasks, setSelectedTasks] = useState(null);

    const [deleteConfirm, setDeleteConfirm] = useState({ open: false, id: '', name: '' });

    const [journalLogs, setJournalLogs] = useState([]);
    const [loadingLogs, setLoadingLogs] = useState(false);
    const [newLogText, setNewLogText] = useState('');
    const [isPostingLog, setIsPostingLog] = useState(false);

    const [siteTasks, setSiteTasks] = useState([]);
    const [loadingTasks, setLoadingTasks] = useState(false);

    const [selectedFiles, setSelectedFiles] = useState([]);
    const [filePreviews, setFilePreviews] = useState([]);
    const fileInputRef = useRef(null);

    useEffect(() => {
        if (selectedJournal) {
            loadJournalLogs(selectedJournal.id);
        }
    }, [selectedJournal]);

    useEffect(() => {
        if (selectedTasks) {
            loadSiteTasks(selectedTasks.id);
        }
    }, [selectedTasks]);

    const handleFileChange = (e) => {
        const files = Array.from(e.target.files);
        if (files.length > 0) {
            setSelectedFiles(prev => [...prev, ...files]);
            files.forEach(file => {
                const reader = new FileReader();
                reader.onloadend = () => {
                    setFilePreviews(prev => [...prev, reader.result]);
                };
                reader.readAsDataURL(file);
            });
        }
    };

    const loadJournalLogs = async (chantierId) => {
        setLoadingLogs(true);
        try {
            const records = await pb.collection('chantiers_photos').getFullList({
                filter: `chantier = "${chantierId}"`,
                sort: '-created',
                expand: 'worker'
            });
            setJournalLogs(records);
        } catch (err) { console.error(err); } finally { setLoadingLogs(false); }
    };

    const loadSiteTasks = async (chantierId) => {
        setLoadingTasks(true);
        try {
            const records = await pb.collection('project_tasks').getFullList({
                filter: `chantier = "${chantierId}"`,
                sort: 'created'
            });
            setSiteTasks(records);
        } catch (err) { console.error(err); } finally { setLoadingTasks(false); }
    };

    const handlePostLog = async () => {
        if (!newLogText.trim() && selectedFiles.length === 0) return;
        setIsPostingLog(true);
        try {
            const formData = new FormData();
            formData.append('chantier', selectedJournal.id);
            formData.append('caption', newLogText);
            formData.append('worker', pb.authStore.model?.id);
            formData.append('type', 'INFO');
            selectedFiles.forEach(file => formData.append('file', file));
            await pb.collection('chantiers_photos').create(formData);
            setNewLogText('');
            setSelectedFiles([]);
            setFilePreviews([]);
            loadJournalLogs(selectedJournal.id);
            toast.success("Mise à jour publiée");
        } catch (err) { toast.error("Erreur publication"); } finally { setIsPostingLog(false); }
    };

    const handleDeleteLog = async (id) => {
        if (!window.confirm("Supprimer cette note ?")) return;
        try {
            await pb.collection('chantiers_photos').delete(id);
            loadJournalLogs(selectedJournal.id);
            toast.success("Note supprimée");
        } catch (err) { toast.error("Erreur suppression"); }
    };

    const handleToggleTask = async (task) => {
        try {
            const next = task.progress === 100 ? 0 : 100;
            await pb.collection('project_tasks').update(task.id, { progress: next });
            loadSiteTasks(selectedTasks.id);
        } catch (err) { toast.error("Erreur tâche"); }
    };

    const loadChantiers = async () => {
        setLoading(true);
        try {
            const isAdmin = userRole === 'admin' || userRole === 'bureau';
            const filter = isAdmin ? '' : `chef_chantier ?~ "${pb.authStore.model?.id}" || workers ?~ "${pb.authStore.model?.id}"`;
            const [records, interventions, timesheets] = await Promise.all([
                pb.collection('chantiers').getFullList({ sort: '-created', filter, expand: 'client' }),
                pb.collection('interventions').getFullList(),
                pb.collection('timesheets').getFullList()
            ]);
            
            const realRecords = records.filter(c => c.description !== 'Projet interne généré automatiquement');

            const chantiersWithHours = realRecords.map(c => {
                const chantierInters = interventions.filter(i => i.project_id === c.id);
                const consumed = chantierInters.reduce((acc, i) => acc + (parseFloat(i.hours_override) || 8), 0);
                
                const chantierTimesheets = timesheets.filter(t => t.chantier === c.id);
                const real_hours = chantierTimesheets.reduce((acc, t) => acc + (t.hours || 0), 0);
                const approved_hours = chantierTimesheets.filter(t => t.status === 'approved').reduce((acc, t) => acc + (t.hours || 0), 0);

                const unique_workers_real = new Set(chantierTimesheets.map(t => t.worker).filter(Boolean)).size;
                const unique_workers_planned = new Set(chantierInters.flatMap(i => i.assigned_staff || []).filter(Boolean)).size;

                return { ...c, planned_hours: consumed, real_hours, approved_hours, unique_workers_real, unique_workers_planned };
            });
            setChantiers(chantiersWithHours);
        } catch (err) { toast.error("Erreur chargement"); } finally { setLoading(false); }
    };

    useEffect(() => { loadChantiers(); }, []);

    const filteredChantiers = useMemo(() => {
        return chantiers.filter(c => {
            const matchesSearch = c.name?.toLowerCase().includes(searchTerm.toLowerCase()) || 
                                 c.expand?.client?.name?.toLowerCase().includes(searchTerm.toLowerCase());
            const matchesStatus = statusFilter === 'ALL' || c.status === statusFilter;
            return matchesSearch && matchesStatus;
        });
    }, [chantiers, searchTerm, statusFilter]);

    const handleCreate = () => { setEditingChantier(null); setIsFormOpen(true); };
    const handleEdit = (c) => { setEditingChantier(c); setIsFormOpen(true); };
    const handleSave = () => { setIsFormOpen(false); loadChantiers(); if (refreshData) refreshData(); };
    
    const handleConfirmDelete = async () => {
        const { id } = deleteConfirm;
        try { 
            await pb.collection('chantiers').delete(id); 
            setDeleteConfirm({ open: false, id: '', name: '' });
            loadChantiers(); 
            if (refreshData) refreshData();
            toast.success("Chantier supprimé"); 
        } catch (err) { toast.error("Erreur de suppression"); }
    };

    const handleDownloadPDF = () => {
        if (!selectedJournal) return;
        let html = `
            <div style="font-family: sans-serif; padding: 40px;">
                <h1>Rapport de Chantier: ${selectedJournal.name}</h1>
                <p>Généré le ${new Date().toLocaleDateString()}</p>
                <hr/>
                <div style="display: flex; flex-direction: column; gap: 20px;">
                    ${journalLogs.map(log => `
                        <div style="border-bottom: 1px solid #eee; padding-bottom: 10px;">
                            <div style="font-weight: bold; color: #3b82f6;">${new Date(log.created).toLocaleString()}</div>
                            <p>${log.caption}</p>
                        </div>
                    `).join('')}
                </div>
            </div>
        `;
        const win = window.open('', '_blank');
        win.document.write(html);
        win.document.close();
        win.print();
    };

    const getStatusColor = (s) => {
        if (s === 'EN_COURS') return '#f59e0b';
        if (s === 'TERMINE') return '#10b981';
        if (s === 'ANNULE') return '#ef4444';
        return '#64748b';
    };

    const getStatusLabel = (s) => {
        if (s === 'EN_COURS') return 'En Cours';
        if (s === 'TERMINE') return 'Terminé';
        if (s === 'PLANNING') return 'Planifié';
        return s;
    };

    const isOverdue = (c) => c.end_date_estimated && new Date(c.end_date_estimated) < new Date() && c.status !== 'TERMINE';

    return (
        <div className={styles.dataSection}>
            <div className={chantiersStyles.chantiersHeader}>
                <div className={chantiersStyles.titleArea}>
                    <h2 style={{ fontSize: '1.8rem', fontWeight: 900 }}>Gestion des Chantiers</h2>
                    <p style={{ margin: 0, color: 'var(--color-text-light, #64748b)' }}>Suivi technique et financier</p>
                </div>
                <div className={chantiersStyles.headerActionsWrapper}>
                    <div style={{ background: 'var(--color-surface, #f1f5f9)', border: '1px solid var(--color-light-gray, transparent)', padding: '4px', borderRadius: '12px', display: 'flex', gap: '4px' }}>
                        <button className={`${styles.toggleBtn} ${viewMode === 'LIST' ? styles.active : ''}`} onClick={() => setViewMode('LIST')}><List size={16} /></button>
                        <button className={`${styles.toggleBtn} ${viewMode === 'GRID' ? styles.active : ''}`} onClick={() => setViewMode('GRID')}><LayoutGrid size={16} /></button>
                        <button className={`${styles.toggleBtn} ${viewMode === 'MAP' ? styles.active : ''}`} onClick={() => setViewMode('MAP')}><MapIcon size={16} /></button>
                    </div>
                    <button className={styles.addButton} onClick={handleCreate}><Plus size={18} /> Nouveau</button>
                </div>
            </div>

            <div className={chantiersStyles.searchFilterBar}>
                <div className={chantiersStyles.searchField}>
                    <Search size={18} className={chantiersStyles.searchIcon} />
                    <input type="text" placeholder="Rechercher..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className={chantiersStyles.searchInput} />
                </div>
                <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className={chantiersStyles.statusSelect}>
                    <option value="ALL">Tous les statuts</option>
                    <option value="PLANNING">Planifiés</option>
                    <option value="EN_COURS">En Cours</option>
                    <option value="TERMINE">Terminés</option>
                </select>
            </div>

            {loading ? <div style={{ textAlign: 'center', padding: '50px' }}>Chargement...</div> : viewMode === 'LIST' ? (
                <div className={styles.tableResponsive}>
                    <table className={styles.dataTable}>
                        <thead>
                            <tr>
                                <th>Chantier</th>
                                <th>Client</th>
                                <th>Équipe</th>
                                <th>Suivi Budget & Heures</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredChantiers.map(c => (
                                <tr key={c.id}>
                                    <td>
                                        <div className={chantiersStyles.chantierName}>
                                            {c.name} {isOverdue(c) && <AlertCircle size={14} color="#ef4444" />}
                                        </div>
                                        <div style={{ fontSize: '0.7rem', color: getStatusColor(c.status), fontWeight: 800 }}>{getStatusLabel(c.status)}</div>
                                    </td>
                                    <td>{c.expand?.client?.name || '-'}</td>
                                    <td>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                            <div className={chantiersStyles.teamBadge} style={{ background: 'var(--color-background, #f8fafc)', border: '1px solid var(--color-light-gray, #e2e8f0)', color: 'var(--color-text-light, #64748b)' }}>
                                                <User size={12} /> {c.unique_workers_planned} planifié(s)
                                            </div>
                                            <div className={chantiersStyles.teamBadge} style={{ background: c.unique_workers_real > 0 ? 'rgba(22, 101, 52, 0.1)' : 'var(--color-background, #f8fafc)', border: '1px solid ' + (c.unique_workers_real > 0 ? 'rgba(22, 101, 52, 0.2)' : 'var(--color-light-gray, #e2e8f0)'), color: c.unique_workers_real > 0 ? '#22c55e' : 'var(--color-text-light, #64748b)' }}>
                                                <User size={12} /> {c.unique_workers_real} intervenu(s)
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        <div className={chantiersStyles.financeBox}>
                                            <div className={chantiersStyles.financeHeader}>
                                                <span><Clock size={12} /> Réel: {c.real_hours}h / Budget: {c.budget_hours || 0}h</span>
                                                <span style={{ color: (c.real_hours > (c.budget_hours || 0)) ? '#ef4444' : '#10b981' }}>{c.budget_hours ? Math.round((c.real_hours / c.budget_hours) * 100) : 0}%</span>
                                            </div>
                                            <div className={chantiersStyles.progressBarTrack}>
                                                <div style={{ width: `${c.budget_hours ? Math.min(100, (c.real_hours / c.budget_hours) * 100) : 0}%`, height: '100%', background: (c.real_hours > (c.budget_hours || 0)) ? '#ef4444' : '#10b981' }} />
                                            </div>
                                            <div style={{ fontSize: '0.75rem', color: 'var(--color-text-light, #64748b)', marginTop: '6px' }}>
                                                Prévu planning : {c.planned_hours}h • Validées : <span style={{ color: '#10b981' }}>{c.approved_hours}h</span>
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        <div style={{ display: 'flex', gap: '8px' }}>
                                            <button onClick={() => setSelectedJournal(c)} className={styles.actionBtn}><Camera size={16} /></button>
                                            <button onClick={() => setSelectedTasks(c)} className={styles.actionBtn}><List size={16} /></button>
                                            <button onClick={() => handleEdit(c)} className={styles.editBtn}><Edit2 size={16} /></button>
                                            <button onClick={() => setDeleteConfirm({ open: true, id: c.id, name: c.name })} className={styles.deleteBtn}><Trash2 size={16} /></button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : viewMode === 'GRID' ? (
                <div className={chantiersStyles.chantierGrid}>
                    {filteredChantiers.map(c => (
                        <div key={c.id} className={chantiersStyles.chantierCard}>
                            <img src={c.photo ? pb.files.getURL(c, c.photo) : 'https://images.unsplash.com/photo-1541888946425-d81bb19240f5?q=80&w=600&auto=format&fit=crop'} alt="" className={chantiersStyles.cardImage} />
                            <div className={chantiersStyles.cardBadge} style={{ background: getStatusColor(c.status) }}>{getStatusLabel(c.status)}</div>
                            <div className={chantiersStyles.cardContent}>
                                <h3 className={chantiersStyles.cardTitle}>{c.name}</h3>
                                <div className={chantiersStyles.cardClient}>{c.expand?.client?.name || 'Sans client'}</div>
                                <div className={chantiersStyles.financeBox} style={{ background: 'var(--color-background, #f8fafc)', padding: '10px', borderRadius: '8px', border: '1px solid var(--color-light-gray, #e2e8f0)', marginTop: '10px' }}>
                                    <div className={chantiersStyles.financeHeader}>
                                        <span>Réel: {c.real_hours}h / Budget: {c.budget_hours || 0}h</span>
                                    </div>
                                    <div className={chantiersStyles.progressBarTrack} style={{ marginBottom: '6px' }}>
                                        <div style={{ width: `${c.budget_hours ? Math.min(100, (c.real_hours / c.budget_hours) * 100) : 0}%`, height: '100%', background: (c.real_hours > (c.budget_hours || 0)) ? '#ef4444' : '#10b981' }} />
                                    </div>
                                    <div style={{ fontSize: '0.75rem', color: '#64748b' }}>
                                        Prévu: {c.planned_hours}h • Validées: {c.approved_hours}h
                                    </div>
                                </div>
                            </div>
                            <div className={chantiersStyles.cardFooter}>
                                <div className={chantiersStyles.cardActions}>
                                    <button onClick={() => setSelectedJournal(c)} className={chantiersStyles.actionCircleBtn} title="Journal"><Camera size={14} /></button>
                                    <button onClick={() => handleEdit(c)} className={chantiersStyles.actionCircleBtn} title="Modifier"><Edit2 size={14} /></button>
                                    <button onClick={() => setDeleteConfirm({ open: true, id: c.id, name: c.name })} className={chantiersStyles.actionCircleBtn} style={{ color: '#ef4444' }} title="Supprimer"><Trash2 size={14} /></button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <div style={{ height: '500px', borderRadius: '20px', overflow: 'hidden' }}>
                    <MapContainer center={[46.6, 1.8]} zoom={6} style={{ height: '100%' }}>
                        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                        {filteredChantiers.map(c => (
                            <Marker key={c.id} position={[c.lat || 46, c.lng || 1.8]} icon={blueIcon}>
                                <Popup><strong>{c.name}</strong><br/>{c.address}</Popup>
                            </Marker>
                        ))}
                    </MapContainer>
                </div>
            )}

            {isFormOpen && <ChantierForm chantier={editingChantier} onSave={handleSave} onCancel={() => setIsFormOpen(false)} />}

            {selectedJournal && (
                <div className={styles.modalOverlay}>
                    <div className={styles.modalContent} style={{ maxWidth: '600px', width: '90%', maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                        <div className={styles.modalHeader} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px' }}>
                            <h3 style={{ margin: 0 }}>Journal: {selectedJournal.name}</h3>
                            <div style={{ display: 'flex', gap: '10px' }}>
                                <button onClick={handleDownloadPDF} style={{ background: 'white', color: '#0f172a', padding: '5px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px' }}>
                                    <FileText size={14} /> PDF
                                </button>
                                <button onClick={() => setSelectedJournal(null)} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer' }}>&times;</button>
                            </div>
                        </div>
                        <div style={{ padding: '20px', flex: 1, overflowY: 'auto' }}>
                            <div className={chantiersStyles.logEntryForm}>
                                <textarea value={newLogText} onChange={e => setNewLogText(e.target.value)} className={chantiersStyles.logTextarea} placeholder="Nouvelle note..." />
                                <div style={{ marginTop: '10px', display: 'flex', justifyContent: 'space-between' }}>
                                    <input type="file" onChange={handleFileChange} multiple style={{ fontSize: '12px' }} />
                                    <button onClick={handlePostLog} disabled={isPostingLog} style={{ background: '#0f172a', color: 'white', padding: '8px 20px', borderRadius: '8px', border: 'none', cursor: 'pointer' }}>{isPostingLog ? '...' : 'Poster'}</button>
                                </div>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                                {journalLogs.map(log => (
                                    <div key={log.id} style={{ borderBottom: '1px solid #f1f5f9', paddingBottom: '15px' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.7rem', color: '#64748b', marginBottom: '5px' }}>
                                            <span>{new Date(log.created).toLocaleString()}</span>
                                            <button onClick={() => handleDeleteLog(log.id)} style={{ color: '#ef4444', border: 'none', background: 'none', cursor: 'pointer' }}><Trash2 size={12} /></button>
                                        </div>
                                        <p style={{ margin: 0, fontSize: '0.9rem' }}>{log.caption}</p>
                                        {log.file && (
                                            <div style={{ marginTop: '10px', display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                                                {(Array.isArray(log.file) ? log.file : [log.file]).map((f, i) => (
                                                    <img key={i} src={pb.files.getURL(log, f)} style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '8px' }} alt="" />
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {selectedTasks && (
                <div className={styles.modalOverlay}>
                    <div className={styles.modalContent} style={{ maxWidth: '500px', width: '90%' }}>
                        <div className={styles.modalHeader} style={{ display: 'flex', justifyContent: 'space-between', padding: '20px' }}>
                            <h3 style={{ margin: 0 }}>Étapes: {selectedTasks.name}</h3>
                            <button onClick={() => setSelectedTasks(null)} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer' }}>&times;</button>
                        </div>
                        <div style={{ padding: '20px' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                {siteTasks.map(t => (
                                    <label key={t.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px', background: '#f8fafc', borderRadius: '10px', cursor: 'pointer' }}>
                                        <input type="checkbox" checked={t.progress === 100} onChange={() => handleToggleTask(t)} />
                                        <span style={{ textDecoration: t.progress === 100 ? 'line-through' : 'none' }}>{t.name}</span>
                                    </label>
                                ))}
                                {siteTasks.length === 0 && <p style={{ textAlign: 'center', color: '#64748b' }}>Aucune étape technique.</p>}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {deleteConfirm.open && createPortal(
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.4)', backdropFilter: 'blur(4px)', zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
                    <div style={{ background: '#fff', width: '100%', maxWidth: 450, borderRadius: 24, padding: 30, boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)', textAlign: 'center' }}>
                        <div style={{ width: 60, height: 60, borderRadius: 20, background: '#fee2e2', color: '#ef4444', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
                            <Trash2 size={28} />
                        </div>
                        <h3 style={{ margin: '0 0 10px', fontSize: '1.25rem', fontWeight: 900 }}>Supprimer le chantier ?</h3>
                        <p style={{ color: '#64748b', fontSize: '0.9rem', lineHeight: 1.5, marginBottom: 30 }}>
                            Êtes-vous sûr de vouloir supprimer <strong>{deleteConfirm.name}</strong> ?<br/>
                            Cette action supprimera également toutes les interventions et données liées. Elle est irréversible.
                        </p>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                            <button 
                                onClick={() => setDeleteConfirm({ open: false, id: '', name: '' })}
                                style={{ padding: '12px', borderRadius: 12, border: '1.5px solid #e2e8f0', background: '#fff', fontWeight: 700, cursor: 'pointer' }}
                            >
                                Annuler
                            </button>
                            <button 
                                onClick={handleConfirmDelete}
                                style={{ padding: '12px', borderRadius: 12, border: 'none', background: '#ef4444', color: '#fff', fontWeight: 800, cursor: 'pointer' }}
                            >
                                Confirmer
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default Chantiers;
